import { Injectable } from '@angular/core';
import { TwilioDeviceChannelMessage, TwilioDeviceCoordinatorHooks } from 'src/app/models/twilio-device.model';
import { environment } from 'src/environments/environment';

const LOCK_PREFIX = 'welby-twilio-device';

// TypeScript 4.6 ships the LockManager types but not `navigator.locks` itself.
const lockManager = (): LockManager | undefined =>
  typeof navigator !== 'undefined' ? (navigator as Navigator & { locks?: LockManager }).locks : undefined;

const randomId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;

/**
 * Keeps one Twilio Device per user across all of their Welby tabs.
 *
 * Why: every tab used to create its own Device with the same identity. With several tabs open the
 * Devices kept dropping their connection (31009) and failed to reconnect with 20101 while the token
 * was still valid; with a single tab that did not happen. So only one tab holds a Device at a time.
 *
 * How:
 * - The Device belongs to whichever tab holds a Web Lock named after the identity. The browser
 *   grants it to one tab only and releases it by itself when that tab closes or crashes.
 * - Every other tab keeps a queued request for the lock, so when the holder closes the next tab
 *   gets the Device without anyone doing anything, and incoming calls keep ringing somewhere.
 * - A tab takes the Device over (`steal`) when the care manager looks at it or places a call from
 *   it. Before stealing it asks the holder over a BroadcastChannel whether it is on a call; if it
 *   is, the Device stays where it is, because taking it would drop that call. A holder that does
 *   not answer in time (closed, frozen in the background) is taken over anyway.
 *
 * Where Web Locks or BroadcastChannel are missing, {@link isSupported} is false and the caller keeps
 * the previous behaviour of one Device per tab.
 */
@Injectable({
  providedIn: 'root',
})
export class TwilioDeviceCoordinatorService {
  private lockName: string | null = null;
  private hooks: TwilioDeviceCoordinatorHooks | null = null;
  private channel: BroadcastChannel | null = null;
  private holding = false;
  /** Increases with every lock request; tells a stale grant or rejection apart from the current one. */
  private grantSeq = 0;
  private activeGrant: number | null = null;
  /** Resolving it releases the lock this tab holds. */
  private releaseHeld: (() => void) | null = null;
  /** Aborts the queued (waiting) lock request, if there is one. */
  private queued: AbortController | null = null;
  private takeover: Promise<boolean> | null = null;
  private readonly pendingProbes = new Map<string, (busy: boolean) => void>();

  get isSupported(): boolean {
    return !!lockManager() && typeof BroadcastChannel !== 'undefined';
  }

  /** True while this tab is the one that holds the user's Device. */
  get isHolder(): boolean {
    return this.holding;
  }

  /**
   * Joins the coordination for `identity`. A tab the care manager is looking at tries to take the
   * Device straight away; a background tab waits in line.
   */
  start(identity: string, hooks: TwilioDeviceCoordinatorHooks): void {
    this.stop();
    this.lockName = `${LOCK_PREFIX}:${identity}`;
    this.hooks = hooks;
    this.channel = new BroadcastChannel(this.lockName);
    this.channel.onmessage = (event: MessageEvent<TwilioDeviceChannelMessage>) => this.onMessage(event.data);

    if (document.visibilityState === 'visible') {
      this.takeOver().then((holds) => {
        if (!holds) {
          this.queue();
        }
      });
    } else {
      this.queue();
    }
  }

  /** Called by the holder when its call ends, so a tab that was refused the Device can take it. */
  notifyIdle(): void {
    if (this.holding) {
      this.channel?.postMessage({ type: 'idle' } as TwilioDeviceChannelMessage);
    }
  }

  /** Leaves the coordination (sign-out): releases the lock and stops listening to other tabs. */
  stop(): void {
    this.lockName = null;
    this.hooks = null;
    // An in-flight takeover belongs to the session being left; it checks the lock name before acting.
    this.takeover = null;
    this.queued?.abort();
    this.queued = null;
    this.holding = false;
    this.activeGrant = null;
    const release = this.releaseHeld;
    this.releaseHeld = null;
    release?.();
    this.channel?.close();
    this.channel = null;
    this.pendingProbes.forEach((finish) => finish(false));
    this.pendingProbes.clear();
  }

  /**
   * Makes this tab the holder of the Device. Resolves `true` when it is (or already was), and `false`
   * when another tab is on a call and keeps the Device.
   */
  takeOver(): Promise<boolean> {
    if (!this.lockName) {
      return Promise.resolve(false);
    }
    if (this.holding) {
      return Promise.resolve(true);
    }
    if (!this.takeover) {
      this.takeover = this.doTakeOver().finally(() => (this.takeover = null));
    }
    return this.takeover;
  }

  private async doTakeOver(): Promise<boolean> {
    const lockName = this.lockName;
    // Nobody holds the Device (the usual single-tab sign-in): take it without waiting for a probe
    // that no one would answer.
    await this.request({ ifAvailable: true });
    if (this.lockName !== lockName) {
      return false;
    }
    if (this.holding) {
      return true;
    }
    const busyElsewhere = await this.probe();
    // The session may have ended, or another one started, while the holder was asked.
    if (this.lockName !== lockName) {
      return false;
    }
    if (this.holding) {
      return true;
    }
    if (busyElsewhere) {
      return false;
    }
    // Accepted race: the holder can start preparing a call in the few milliseconds between its
    // "not busy" answer and this steal. Its Device is then destroyed and that dial fails.
    // The queued request would otherwise be granted later on top of the stolen lock.
    this.queued?.abort();
    this.queued = null;
    await this.request({ steal: true });
    return this.holding;
  }

  /** Waits in line for the lock, so this tab picks the Device up when the holder goes away. */
  private queue(): void {
    if (!this.lockName || this.holding || this.queued) {
      return;
    }
    const controller = new AbortController();
    this.queued = controller;
    this.request({ signal: controller.signal });
  }

  /**
   * Requests the lock and resolves once it is granted (or the request ends without a grant). The
   * lock is then held until {@link stop} resolves `releaseHeld` or another tab steals it.
   */
  private request(options: LockOptions): Promise<void> {
    const lockName = this.lockName;
    const grant = ++this.grantSeq;
    return new Promise<void>((granted) => {
      lockManager()
        .request(lockName, options, (lock) => {
          // `null` when an `ifAvailable` request found the lock taken.
          if (!lock || this.lockName !== lockName) {
            granted();
            return undefined;
          }
          // Granted through another request than the queued one: the queued one would otherwise be
          // granted later, on top of this.
          if (this.queued && this.queued.signal !== options.signal) {
            this.queued.abort();
          }
          this.queued = null;
          this.holding = true;
          this.activeGrant = grant;
          const held = new Promise<void>((release) => (this.releaseHeld = release));
          granted();
          this.hooks?.onAcquired();
          return held;
        })
        .then(
          () => granted(),
          () => {
            granted();
            // A request that was holding the lock and now rejects was stolen by another tab. One
            // that was still waiting was aborted by this tab, which already knows.
            if (this.activeGrant === grant && this.holding) {
              this.lose();
            }
          }
        );
    });
  }

  private lose(): void {
    this.holding = false;
    this.activeGrant = null;
    this.releaseHeld = null;
    this.hooks?.onLost();
    this.queue();
  }

  /** Asks the holder whether it is on a call. No answer in time counts as "not busy". */
  private probe(): Promise<boolean> {
    const channel = this.channel;
    if (!channel) {
      return Promise.resolve(false);
    }
    const requestId = randomId();
    return new Promise<boolean>((resolve) => {
      const timer = setTimeout(() => finish(false), environment.twilioDevice.takeoverProbeTimeoutMs);
      const finish = (busy: boolean) => {
        clearTimeout(timer);
        this.pendingProbes.delete(requestId);
        resolve(busy);
      };
      this.pendingProbes.set(requestId, finish);
      channel.postMessage({ type: 'probe', requestId } as TwilioDeviceChannelMessage);
    });
  }

  private onMessage(message: TwilioDeviceChannelMessage): void {
    if (message?.type === 'probe' && this.holding && this.hooks) {
      this.channel?.postMessage({
        type: 'probe-reply',
        requestId: message.requestId,
        busy: this.hooks.isBusy(),
      } as TwilioDeviceChannelMessage);
    } else if (message?.type === 'probe-reply') {
      this.pendingProbes.get(message.requestId)?.(message.busy);
    } else if (message?.type === 'idle' && !this.holding) {
      this.hooks?.onHolderIdle();
    }
  }
}
